/**
 * Vocabulario canónico de 29100 Cloud.
 *
 * Estos valores son la única fuente de verdad compartida entre la base de
 * datos (CHECK constraints), la API y el frontend. Cualquier cambio aquí debe
 * reflejarse en `migrations/`.
 */

export const USER_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  BLOCKED: 'blocked',
  DELETED: 'deleted',
} as const;
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

/**
 * Estado de servicio de un espacio.
 *
 * `suspended` es sólo lectura: se entra, se consulta y se descarga, pero no se
 * sube ni se comparte. `blocked` ya no deja entrar, y es el paso previo al
 * borrado por impago — nunca un castigo por sí solo.
 */
export const WORKSPACE_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  BLOCKED: 'blocked',
} as const;
export type WorkspaceStatus = (typeof WORKSPACE_STATUS)[keyof typeof WORKSPACE_STATUS];

export const WORKSPACE_TYPE = {
  PERSONAL: 'personal',
  ORGANIZATION: 'organization',
} as const;
export type WorkspaceType = (typeof WORKSPACE_TYPE)[keyof typeof WORKSPACE_TYPE];

export const ORGANIZATION_STATUS = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  DELETED: 'deleted',
} as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUS)[keyof typeof ORGANIZATION_STATUS];

export const ORGANIZATION_ROLE = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
  VIEWER: 'VIEWER',
} as const;
export type OrganizationRole = (typeof ORGANIZATION_ROLE)[keyof typeof ORGANIZATION_ROLE];

export const MEMBER_STATUS = {
  INVITED: 'invited',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REMOVED: 'removed',
} as const;
export type MemberStatus = (typeof MEMBER_STATUS)[keyof typeof MEMBER_STATUS];

/**
 * Ciclo de vida de un objeto en R2.
 *
 * `pending` -> `uploading` -> `processing` -> `available`
 * En cualquier punto puede pasar a `failed`, `quarantined` o `malicious`.
 */
export const FILE_STORAGE_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  AVAILABLE: 'available',
  QUARANTINED: 'quarantined',
  MALICIOUS: 'malicious',
  FAILED: 'failed',
  DELETED: 'deleted',
} as const;
export type FileStorageStatus = (typeof FILE_STORAGE_STATUS)[keyof typeof FILE_STORAGE_STATUS];

export const SHARED_LINK_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  DISABLED: 'disabled',
} as const;
export type SharedLinkStatus = (typeof SHARED_LINK_STATUS)[keyof typeof SHARED_LINK_STATUS];

export const SHARED_LINK_ACCESS_TYPE = {
  VIEW: 'VIEW',
  PREVIEW: 'PREVIEW',
  DOWNLOAD: 'DOWNLOAD',
  PASSWORD_FAILED: 'PASSWORD_FAILED',
  DENIED: 'DENIED',
} as const;
export type SharedLinkAccessType =
  (typeof SHARED_LINK_ACCESS_TYPE)[keyof typeof SHARED_LINK_ACCESS_TYPE];

export const RESOURCE_TYPE = {
  WORKSPACE: 'WORKSPACE',
  FOLDER: 'FOLDER',
  FILE: 'FILE',
  ORGANIZATION: 'ORGANIZATION',
  SHARED_LINK: 'SHARED_LINK',
  USER: 'USER',
  /** Lista de verificación del espacio (fase 14). */
  CHECKLIST: 'CHECKLIST',
  /** Solicitud de documentos (fase 15). */
  DOCUMENT_REQUEST: 'DOCUMENT_REQUEST',
  /** Comprobante de lo contratado (fase 17). */
  INVOICE: 'INVOICE',
} as const;
export type ResourceType = (typeof RESOURCE_TYPE)[keyof typeof RESOURCE_TYPE];

export const PRODUCT_CODE = {
  PERSONAL: 'PERSONAL',
  BUSINESS: 'BUSINESS',
} as const;
export type ProductCode = (typeof PRODUCT_CODE)[keyof typeof PRODUCT_CODE];

export const PLAN_CODE = {
  PERSONAL_FREE: 'PERSONAL_FREE',
  PERSONAL: 'PERSONAL',
  PERSONAL_PLUS: 'PERSONAL_PLUS',
  PERSONAL_FAMILY: 'PERSONAL_FAMILY',
  BUSINESS_FREE: 'BUSINESS_FREE',
  BUSINESS_STARTER: 'BUSINESS_STARTER',
  BUSINESS_PRO: 'BUSINESS_PRO',
  BUSINESS_ENTERPRISE: 'BUSINESS_ENTERPRISE',
} as const;
export type PlanCode = (typeof PLAN_CODE)[keyof typeof PLAN_CODE];

/**
 * Qué tipo de cliente está iniciando sesión.
 *
 * Decide **cómo se entrega la credencial**, que es lo único que cambia entre
 * los dos: la misma sesión, la misma fila en `sessions`, la misma caducidad y
 * la misma revocación.
 *
 *   * `browser` recibe cookies (`HttpOnly` la de sesión, legible la de CSRF).
 *     Es lo correcto ahí porque el navegador las envía solo y la de sesión
 *     queda fuera del alcance de cualquier XSS.
 *   * `native` recibe el token en el cuerpo y **ninguna cookie**. Una
 *     aplicación no tiene un `document` que proteger y sí un llavero del
 *     sistema donde guardarlo; y sin cookie no hay credencial ambiental, que
 *     es justo por lo que un Bearer está exento de CSRF.
 *
 * Exactamente una credencial por cliente. Emitir las dos dejaría a la
 * aplicación guardando una cookie de CSRF que no usa nadie, y a un contenedor
 * web con dos formas de autenticarse que pueden caducar por separado.
 */
export const AUTH_CLIENT = {
  BROWSER: 'browser',
  NATIVE: 'native',
} as const;
export type AuthClient = (typeof AUTH_CLIENT)[keyof typeof AUTH_CLIENT];

export const BILLING_INTERVAL = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  NONE: 'none',
} as const;
export type BillingInterval = (typeof BILLING_INTERVAL)[keyof typeof BILLING_INTERVAL];

export const SUBSCRIPTION_STATUS = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  EXPIRED: 'expired',
} as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

/**
 * Estados en los que una suscripción todavía habilita el servicio.
 * `past_due` sigue habilitado durante el periodo de gracia.
 */
export const ENTITLED_SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  SUBSCRIPTION_STATUS.TRIALING,
  SUBSCRIPTION_STATUS.ACTIVE,
  SUBSCRIPTION_STATUS.PAST_DUE,
];

/**
 * Feature flags evaluadas en backend contra `plan_features`.
 * NUNCA replicar estas reglas en React: el frontend sólo pinta lo que la API
 * le entrega en `GET /api/plans/entitlements`.
 */
export const FEATURE_CODE = {
  SHARED_LINKS: 'shared_links',
  PASSWORD_LINKS: 'password_links',
  FOLDER_SHARING: 'folder_sharing',
  LINK_EXPIRATION: 'link_expiration',
  PHOTO_GALLERY: 'photo_gallery',
  PHOTO_BACKUP: 'photo_backup',
  FILE_VERSIONS: 'file_versions',
  ADVANCED_PERMISSIONS: 'advanced_permissions',
  AUDIT_LOGS: 'audit_logs',
  ORGANIZATIONS: 'organizations',
  TEAMS: 'teams',
  CUSTOM_BRANDING: 'custom_branding',
  HISTORY_DAYS: 'history_days',
  TRASH_RETENTION_DAYS: 'trash_retention_days',
  MAX_SHARED_LINKS: 'max_shared_links',
  MAX_USERS: 'max_users',
  /** Espacios de empresa que puede tener abiertos el titular de la suscripción. */
  MAX_WORKSPACES: 'max_workspaces',
  /** Portal de carga: pedir documentos a alguien sin cuenta (fase 15). */
  DOCUMENT_REQUESTS: 'document_requests',
  MAX_DOCUMENT_REQUESTS: 'max_document_requests',
  /**
   * La aplicación de móvil: descargarla y entrar con ella.
   *
   * Es una función de plan y no un regalo a todo el mundo, y la razón es de
   * coste: una app en el bolsillo sube fotos desde donde esté, así que
   * multiplica la utilización de la cuota justo en los planes cuyo margen
   * depende de que el cliente no la llene. Se ofrece desde Personal Plus y
   * desde Business Pro, que son los primeros escalones que aguantan esa
   * utilización sin venderse con pérdida.
   *
   * Gobierna las dos mitades de la misma cosa: que el panel ofrezca la
   * descarga, y que un cliente no navegador pueda iniciar sesión. Separarlas
   * dejaría un APK repartido que luego no entra, que es peor que no ofrecerlo.
   */
  MOBILE_APP: 'mobile_app',
} as const;
export type FeatureCode = (typeof FEATURE_CODE)[keyof typeof FEATURE_CODE];

/**
 * Retención de papelera por plan (días). Valores por defecto: la fuente de
 * verdad en runtime es `plan_features.trash_retention_days`, configurable sin
 * desplegar código.
 */
export const DEFAULT_TRASH_RETENTION_DAYS: Record<PlanCode, number> = {
  PERSONAL_FREE: 7,
  PERSONAL: 30,
  PERSONAL_PLUS: 30,
  PERSONAL_FAMILY: 30,
  BUSINESS_FREE: 14,
  BUSINESS_STARTER: 90,
  BUSINESS_PRO: 90,
  BUSINESS_ENTERPRISE: 90,
};

/**
 * Espacios de empresa por plan. Es el cupo del TITULAR, no del espacio: cuenta
 * los que ya tiene abiertos, incluido aquel sobre el que corre la suscripción.
 * `null` = sin límite comercial (queda el tope técnico de la plataforma).
 *
 * Vale uno en todos los planes porque **lo gratuito es de la persona, no del
 * espacio**: cada quien estrena la plataforma una vez —su espacio personal y un
 * Business de prueba— y a partir de ahí cada espacio nuevo trae su propia
 * suscripción. Un plan de pago que incluyera 3 ó 10 espacios sonaría generoso y
 * en realidad regalaría un plan gratuito, con su cuota, por cada uno.
 * `BUSINESS_ENTERPRISE` es la excepción: se negocia y ya pasa por un
 * administrador, así que ahí manda el tope técnico.
 *
 * Igual que la retención de papelera, la fuente de verdad en runtime es
 * `plan_features.max_workspaces`; esto es el respaldo para una base sin sembrar
 * y el valor de referencia del catálogo. Sin respaldo, un plan sin fila daría
 * "sin límite" —justo lo que este cupo existe para impedir—, así que el que
 * falta se trata como el del plan gratuito.
 */
export const DEFAULT_MAX_WORKSPACES: Record<PlanCode, number | null> = {
  PERSONAL_FREE: 1,
  PERSONAL: 1,
  PERSONAL_PLUS: 1,
  PERSONAL_FAMILY: 1,
  BUSINESS_FREE: 1,
  BUSINESS_STARTER: 1,
  BUSINESS_PRO: 1,
  BUSINESS_ENTERPRISE: null,
};

/** Cupo aplicado a quien no tiene ninguna suscripción legible. */
export const FALLBACK_MAX_WORKSPACES = 1;

/**
 * Si una notificación además debe salir por correo, y en qué punto está.
 *
 * **Existe antes que el correo, y es deliberado.** No hace falta cola ni
 * proveedor: la notificación se escribe hoy y queda marcada como pendiente. El
 * día que exista remitente, una consulta drena las pendientes y las sella.
 * Hoy esa consulta no devuelve nada y no cuesta nada.
 *
 * `none` es el valor por defecto **a propósito**. Si fuera `pending`, el día que
 * se conecte el correo saldrían de golpe meses de avisos viejos hacia buzones
 * reales. Sólo se marca lo que de verdad merece un correo.
 */
export const NOTIFICATION_EMAIL_STATUS = {
  /** No se manda por correo. La inmensa mayoría. */
  NONE: 'none',
  /** En cola, esperando a que exista remitente. */
  PENDING: 'pending',
  SENT: 'sent',
  FAILED: 'failed',
} as const;

export type NotificationEmailStatus =
  (typeof NOTIFICATION_EMAIL_STATUS)[keyof typeof NOTIFICATION_EMAIL_STATUS];

export const NOTIFICATION_TYPE = {
  FILE_SHARED: 'FILE_SHARED',
  LINK_CREATED: 'LINK_CREATED',
  LINK_EXPIRED: 'LINK_EXPIRED',
  STORAGE_WARNING: 'STORAGE_WARNING',
  STORAGE_FULL: 'STORAGE_FULL',
  SUBSCRIPTION_CHANGED: 'SUBSCRIPTION_CHANGED',
  ORGANIZATION_INVITE: 'ORGANIZATION_INVITE',
  /** A quien invitó: la persona aceptó y ya está dentro. */
  ORGANIZATION_INVITE_ACCEPTED: 'ORGANIZATION_INVITE_ACCEPTED',
  /** A quien invitó: la persona dijo que no. Sin esto se queda esperando. */
  ORGANIZATION_INVITE_DECLINED: 'ORGANIZATION_INVITE_DECLINED',
  /** A quien se expulsa: enterarse por dejar de ver la organización es peor. */
  ORGANIZATION_MEMBER_REMOVED: 'ORGANIZATION_MEMBER_REMOVED',
  /** A quien recibe acceso: una carpeta que aparece sin avisar no se usa. */
  /** Al cliente: canceló, y hasta cuándo sigue teniendo el plan. */
  SUBSCRIPTION_CANCELED: 'SUBSCRIPTION_CANCELED',
  SUBSCRIPTION_DOWNGRADED: 'SUBSCRIPTION_DOWNGRADED',
  /** Al cliente: excede la cuota y cuánto le queda para arreglarlo. */
  QUOTA_GRACE_STARTED: 'QUOTA_GRACE_STARTED',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  TENANT_REACTIVATED: 'TENANT_REACTIVATED',
  /** Al cliente: el espacio dejó de ser accesible y cuánto queda para el borrado. */
  TENANT_BLOCKED: 'TENANT_BLOCKED',
  /** Al cliente: última llamada antes de que el espacio se borre. */
  TENANT_DELETION_WARNING: 'TENANT_DELETION_WARNING',
  /**
   * Al cliente: un operador programó el borrado de su espacio, y para cuándo.
   *
   * Va aparte de `TENANT_DELETION_WARNING` —el último aviso del plazo de
   * impago— porque el remedio es distinto y es lo único que el cliente puede
   * hacer al leerlo: aquello se evita pagando, y esto no se evita pagando, se
   * evita llamando. Con la fecha delante, quien no vaya a reclamar al menos
   * sabe hasta cuándo tiene para descargar lo suyo.
   */
  TENANT_DELETION_SCHEDULED: 'TENANT_DELETION_SCHEDULED',
  /** Al cliente: el borrado programado se anuló y el espacio sigue en pie. */
  TENANT_DELETION_CANCELED: 'TENANT_DELETION_CANCELED',
  QUOTA_ENFORCED: 'QUOTA_ENFORCED',
  /**
   * Al cliente: su plan vence pronto y hay que renovarlo.
   *
   * Es el aviso que faltaba. Sin él, una suscripción pagada por adelantado
   * vencía en silencio y el primer mensaje que recibía el cliente era que
   * estaba en mora por algo que nadie le pidió renovar.
   */
  SUBSCRIPTION_EXPIRING: 'SUBSCRIPTION_EXPIRING',
  /**
   * Toca revisar una carpeta.
   *
   * Es el primer aviso que el cliente **se pone a sí mismo**: todos los demás
   * los decide el sistema —se acabó el espacio, venció el plan, alguien
   * compartió algo—. Éste lo programa quien va a recibirlo, y por eso es el que
   * menos se puede permitir llegar tarde o repetido.
   */
  REVIEW_DUE: 'REVIEW_DUE',
  FOLDER_SHARED: 'FOLDER_SHARED',
  FOLDER_ACCESS_REVOKED: 'FOLDER_ACCESS_REVOKED',
  /** Al cliente: el administrador ya envió el link de pago. */
  PLAN_REQUEST_LINK_SENT: 'PLAN_REQUEST_LINK_SENT',
  /** Al cliente: se comprobó el pago y el plan quedó activo. */
  PLAN_REQUEST_APPROVED: 'PLAN_REQUEST_APPROVED',
  /** Al titular: el destinatario completó todo lo que se le pidió (fase 15). */
  DOCUMENT_REQUEST_COMPLETED: 'DOCUMENT_REQUEST_COMPLETED',
  /** Al cliente: no se pudo comprobar el pago. */
  PLAN_REQUEST_REJECTED: 'PLAN_REQUEST_REJECTED',
  /** A los administradores: hay una solicitud nueva que atender. */
  PLAN_REQUEST_RECEIVED: 'PLAN_REQUEST_RECEIVED',
  /** A los administradores: el cliente declara haber pagado. */
  PLAN_REQUEST_PAID_DECLARED: 'PLAN_REQUEST_PAID_DECLARED',
} as const;
export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE];

export const AUDIT_ACTION = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  REGISTER: 'REGISTER',
  UPLOAD: 'UPLOAD',
  DOWNLOAD: 'DOWNLOAD',
  DELETE: 'DELETE',
  RESTORE: 'RESTORE',
  SHARE: 'SHARE',
  LINK_CREATED: 'LINK_CREATED',
  LINK_ACCESSED: 'LINK_ACCESSED',
  LINK_DISABLED: 'LINK_DISABLED',
  PERMISSION_CHANGED: 'PERMISSION_CHANGED',
  USER_INVITED: 'USER_INVITED',
  SUBSCRIPTION_CHANGED: 'SUBSCRIPTION_CHANGED',
  PLAN_REQUESTED: 'PLAN_REQUESTED',
  PLAN_REQUEST_LINK_SENT: 'PLAN_REQUEST_LINK_SENT',
  PLAN_REQUEST_PAID_DECLARED: 'PLAN_REQUEST_PAID_DECLARED',
  PLAN_REQUEST_APPROVED: 'PLAN_REQUEST_APPROVED',
  PLAN_REQUEST_REJECTED: 'PLAN_REQUEST_REJECTED',
  USER_STATUS_CHANGED: 'USER_STATUS_CHANGED',
  ACCOUNT_DELETION_REQUESTED: 'ACCOUNT_DELETION_REQUESTED',
  ACCOUNT_DELETION_CANCELED: 'ACCOUNT_DELETION_CANCELED',
  ACCOUNT_ANONYMIZED: 'ACCOUNT_ANONYMIZED',
  DATA_EXPORTED: 'DATA_EXPORTED',
  MFA_ENABLED: 'MFA_ENABLED',
  MFA_DISABLED: 'MFA_DISABLED',
  MFA_RECOVERY_USED: 'MFA_RECOVERY_USED',
  MFA_RECOVERY_CODES_REGENERATED: 'MFA_RECOVERY_CODES_REGENERATED',
  MFA_CHALLENGE_FAILED: 'MFA_CHALLENGE_FAILED',
  SUBSCRIPTION_CANCELED: 'SUBSCRIPTION_CANCELED',
  SUBSCRIPTION_REACTIVATED: 'SUBSCRIPTION_REACTIVATED',
  SUBSCRIPTION_DOWNGRADED: 'SUBSCRIPTION_DOWNGRADED',
  QUOTA_ENFORCED: 'QUOTA_ENFORCED',
  FOLDER_RESTRICTED: 'FOLDER_RESTRICTED',
  FOLDER_UNRESTRICTED: 'FOLDER_UNRESTRICTED',
  PERMISSION_GRANTED: 'PERMISSION_GRANTED',
  PERMISSION_REVOKED: 'PERMISSION_REVOKED',
  INVITATION_ACCEPTED: 'INVITATION_ACCEPTED',
  INVITATION_DECLINED: 'INVITATION_DECLINED',
  MEMBER_REMOVED: 'MEMBER_REMOVED',
  ALBUM_CREATED: 'ALBUM_CREATED',
  ALBUM_UPDATED: 'ALBUM_UPDATED',
  ALBUM_DELETED: 'ALBUM_DELETED',
  AVATAR_CHANGED: 'AVATAR_CHANGED',
  CHECKLIST_CREATED: 'CHECKLIST_CREATED',
  CHECKLIST_UPDATED: 'CHECKLIST_UPDATED',
  CHECKLIST_DELETED: 'CHECKLIST_DELETED',
  DOCUMENT_REQUEST_CREATED: 'DOCUMENT_REQUEST_CREATED',
  DOCUMENT_REQUEST_CLOSED: 'DOCUMENT_REQUEST_CLOSED',
  DOCUMENT_REQUEST_DELETED: 'DOCUMENT_REQUEST_DELETED',
  /** Subida por el portal público. La única acción sin actor conocido. */
  DOCUMENT_REQUEST_UPLOAD: 'DOCUMENT_REQUEST_UPLOAD',
  TENANT_SUSPENDED: 'TENANT_SUSPENDED',
  TENANT_REACTIVATED: 'TENANT_REACTIVATED',
  TENANT_PLAN_CHANGED: 'TENANT_PLAN_CHANGED',
  /**
   * Un operador movió la fecha de fin de la vigencia.
   *
   * Aparte de `TENANT_PLAN_CHANGED` porque contestan preguntas distintas:
   * «¿quién le regaló dos meses?» no se responde igual que «¿quién le cambió el
   * plan?». Los metadatos guardan la fecha anterior y la nueva, que es lo único
   * que permite deshacerlo.
   */
  TENANT_PERIOD_ADJUSTED: 'TENANT_PERIOD_ADJUSTED',
  /**
   * Un operador concedió un plan en modo prueba.
   *
   * Una prueba que nunca se cobró tiene que poder distinguirse de una venta:
   * si las dos quedaran como el mismo asiento, no habría forma de auditar
   * cuánto servicio se está regalando.
   */
  TENANT_TRIAL_STARTED: 'TENANT_TRIAL_STARTED',
  TENANT_BLOCKED: 'TENANT_BLOCKED',
  /**
   * Un operador programó el borrado de un espacio desde el panel.
   *
   * Es el asiento que explica un `TENANT_DELETED` que llegará quince o treinta
   * días después, cuando ya no quede fila que mirar: guarda quién lo pidió, con
   * qué motivo, cuántos archivos había y para qué día quedó. Sin él, el borrado
   * aparecería en la auditoría sin autor, indistinguible del que ejecuta el
   * plazo de impago.
   */
  TENANT_DELETION_SCHEDULED: 'TENANT_DELETION_SCHEDULED',
  /** El borrado programado se anuló: por un operador, o porque volvieron a entrar. */
  TENANT_DELETION_CANCELED: 'TENANT_DELETION_CANCELED',
  TENANT_DELETED: 'TENANT_DELETED',
  WORKSPACE_CREATED: 'WORKSPACE_CREATED',
  ORGANIZATION_CREATED: 'ORGANIZATION_CREATED',
  /**
   * El dinero deja rastro propio, aparte del de la solicitud (fase 17).
   *
   * Se emite la factura al aprobar —lo contratado— y se registra el cobro
   * —el dinero—. Son dos acciones y no una porque una factura puede quedarse
   * sin cobrar, y saber cuál de las dos cosas pasó es justo el dato que hace
   * falta cuando alguien reclama.
   */
  INVOICE_ISSUED: 'INVOICE_ISSUED',
  PAYMENT_RECORDED: 'PAYMENT_RECORDED',
  /** Fechas de revisión de carpetas (fase 18). */
  /** Una carpeta pasó a ser archivo permanente, o dejó de serlo (fase 19). */
  FOLDER_ARCHIVE_CHANGED: 'FOLDER_ARCHIVE_CHANGED',
  FOLDER_REVIEW_SET: 'FOLDER_REVIEW_SET',
  FOLDER_REVIEW_CLEARED: 'FOLDER_REVIEW_CLEARED',
  FOLDER_REVIEW_COMPLETED: 'FOLDER_REVIEW_COMPLETED',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

export const REPORT_REASON = {
  MALWARE: 'MALWARE',
  PHISHING: 'PHISHING',
  COPYRIGHT: 'COPYRIGHT',
  ILLEGAL_CONTENT: 'ILLEGAL_CONTENT',
  SPAM: 'SPAM',
  ABUSE: 'ABUSE',
  OTHER: 'OTHER',
} as const;
export type ReportReason = (typeof REPORT_REASON)[keyof typeof REPORT_REASON];

export const REPORT_STATUS = {
  OPEN: 'open',
  REVIEWING: 'reviewing',
  RESOLVED: 'resolved',
  REJECTED: 'rejected',
} as const;
export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];

/** Nombre de la cookie de sesión. */
export const SESSION_COOKIE_NAME = '29100_session';
/** Cookie del token CSRF (double submit). Legible por el frontend. */
export const CSRF_COOKIE_NAME = '29100_csrf';
/** Cabecera donde el frontend reenvía el token CSRF. */
export const CSRF_HEADER_NAME = 'x-csrf-token';

/** Umbral (%) a partir del cual se notifica STORAGE_WARNING. */
export const STORAGE_WARNING_THRESHOLD = 0.9;

/**
 * Ciclo de vida de una solicitud de plan.
 *
 * Cobro manual asistido: mientras no hay pasarela, subir de plan es una
 * conversación con pasos verificables. El orden es estricto y sólo
 * `APPROVED` —que pone un administrador tras comprobar el ingreso— activa
 * nada. Que el cliente declare el pago es una afirmación suya, no un hecho.
 */
export const PLAN_REQUEST_STATUS = {
  /** El cliente pidió el plan; falta que el administrador envíe el link. */
  REQUESTED: 'REQUESTED',
  /** Link enviado; se espera el pago. */
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  /** El cliente dice haber pagado; falta comprobarlo. */
  PAID_DECLARED: 'PAID_DECLARED',
  /** Pago comprobado y plan activado. */
  APPROVED: 'APPROVED',
  /** No se pudo comprobar el pago. */
  REJECTED: 'REJECTED',
  /** El cliente se echó atrás. */
  CANCELED: 'CANCELED',
} as const;
export type PlanRequestStatus = (typeof PLAN_REQUEST_STATUS)[keyof typeof PLAN_REQUEST_STATUS];

/** Estados en los que la solicitud sigue esperando algo de alguien. */
export const OPEN_PLAN_REQUEST_STATUSES: readonly PlanRequestStatus[] = [
  PLAN_REQUEST_STATUS.REQUESTED,
  PLAN_REQUEST_STATUS.AWAITING_PAYMENT,
  PLAN_REQUEST_STATUS.PAID_DECLARED,
];

/** Medios de cobro admitidos mientras no hay pasarela. */
export const PAYMENT_METHOD = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  MERCADO_PAGO: 'MERCADO_PAGO',
  PAYPAL: 'PAYPAL',
  OTHER: 'OTHER',
} as const;
export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  BANK_TRANSFER: 'Transferencia bancaria',
  MERCADO_PAGO: 'Mercado Pago',
  PAYPAL: 'PayPal',
  OTHER: 'Otro medio',
};

/**
 * Periodo de gracia antes de anonimizar una cuenta.
 *
 * 30 días es el equilibrio entre dos errores: cero días convierte un clic mal
 * dado en una pérdida irreversible, y un plazo largo mantiene datos personales
 * de alguien que ya pidió irse. Durante la espera la cuenta sigue siendo suya
 * y puede cancelar desde Ajustes.
 */
/**
 * Días que un espacio puede exceder su cuota tras una rebaja de plan antes de
 * que el exceso se mande a la papelera.
 *
 * Treinta, la misma cifra que la baja de cuenta: es tiempo de sobra para
 * descargar lo que haga falta o volver a pagar, y no deja una factura de
 * almacenamiento abierta indefinidamente.
 */
export const QUOTA_GRACE_DAYS = 30;

/**
 * A cuántos días del vencimiento se avisa de que el plan se acaba.
 *
 * **Dos avisos y no uno.** El primero, a quince días, llega con tiempo de sobra
 * para que una empresa tramite una transferencia sin prisa —que en un cobro
 * manual es exactamente el cuello de botella—. El segundo, a cinco, es para
 * quien archivó el primero y se olvidó, que es la mayoría.
 *
 * **Y dos y no tres**, porque el tercero convierte el aviso en ruido y el ruido
 * se ignora entero: quien recibe cuatro recordatorios del mismo cobro deja de
 * mirar la campanita, y entonces tampoco ve el que importa.
 *
 * **En orden ascendente, y el orden es parte de la corrección.** Se recorren del
 * umbral más cercano al más lejano y se manda sólo el primero que encaje. Al
 * revés, un cliente que vence mañana cumpliría las dos condiciones —faltan
 * menos de quince días y menos de cinco— y recibiría los dos avisos en la misma
 * ejecución del cron, que es exactamente el ruido que esto evita.
 */
export const SUBSCRIPTION_EXPIRY_NOTICE_DAYS = [5, 15] as const;

/**
 * Días de cortesía entre que vence el periodo pagado y la suscripción caduca.
 *
 * Tres, y no cero. En un cobro manual la renovación es una transferencia que
 * alguien tiene que ordenar y que otro tiene que ver entrar: cortar el mismo
 * día del vencimiento castigaría a quien pagó el viernes por la tarde. Pasados
 * los tres días la suscripción queda en `expired` y **ahí empieza a contar el
 * plazo de impago** de `UNPAID_DAYS`, no antes.
 *
 * Estaba escrito a mano dentro de la consulta que caduca suscripciones. Que
 * viva aquí es lo que permite que la pantalla del cliente enseñe la fecha
 * exacta en la que su espacio pasa a estar en mora, en lugar de una fecha
 * parecida calculada por otro sitio.
 */
export const SUBSCRIPTION_EXPIRY_GRACE_DAYS = 3;

/**
 * Marcador que distingue la suspensión por impago de cualquier otra.
 *
 * No es texto para leer: se guarda en `workspaces.suspension_reason` y es lo
 * que permite que un pago levante **sólo** la suspensión que puso el plazo de
 * impago. Un espacio que un operador suspendió por abuso o por una
 * investigación no puede reactivarse solo porque entró un recibo.
 *
 * Vive aquí y no en el servicio del cron porque lo escriben dos sitios —el cron
 * al suspender y el panel al cobrar— y una cadena copiada a mano en el segundo
 * convertiría ese `CASE` en una condición que nunca se cumple, sin error y sin
 * ruido.
 */
export const UNPAID_SUSPENSION_REASON = 'IMPAGO';

export const ACCOUNT_DELETION_GRACE_DAYS = 30;

/**
 * Plazo del impago, en días desde `workspaces.unpaid_since`.
 *
 * Un espacio impagado guardaba bytes indefinidamente sin ingreso: era el mayor
 * gasto sin control del sistema. Ahora el plazo termina siempre, y termina en
 * un borrado definitivo, así que cada paso avisa antes de darse.
 *
 * Los diez días entre `BLOCK` y `DELETE` son cortos, y conviene tenerlo
 * presente: quien no reaccione a los dos primeros avisos pierde sus archivos
 * sin poder exportarlos, porque para entonces ya no entra. El bloqueo es por
 * espacio y no por cuenta, de modo que nadie pierde el acceso a su espacio
 * personal por una factura de su empresa.
 */
export const UNPAID_DAYS = {
  /** Sólo lectura: entra, consulta y descarga. */
  SUSPEND: 30,
  /** Sin acceso al espacio. */
  BLOCK: 60,
  /** Borrado definitivo de objetos y filas. */
  DELETE: 70,
} as const;

/**
 * Marcador de la suspensión que pone un borrado programado.
 *
 * Cumple el mismo papel que `UNPAID_SUSPENSION_REASON` y por el mismo motivo:
 * anular el borrado tiene que devolver el espacio a la vida **sin levantar**
 * una suspensión que puso otra cosa —un impago, una investigación—. Sin un
 * marcador propio, cancelar un borrado reactivaría a un moroso.
 */
export const TENANT_DELETION_SUSPENSION_REASON = 'BORRADO_PROGRAMADO';

/**
 * Plazo entre que un operador borra un espacio desde el panel y el borrado
 * definitivo, en días.
 *
 * Son dos plazos y no uno porque no se está borrando lo mismo:
 *
 *   * **Vacío, 15 días.** Un espacio sin un solo archivo no tiene nada que
 *     rescatar, así que el plazo no existe para dar tiempo a exportar: existe
 *     para dar tiempo a **volver**. Por eso el espacio se queda activo y
 *     **entrar lo salva** —el cron anula el borrado si alguien inició sesión
 *     después de programarlo—: quien se registró, no llegó a usarlo y aparece a
 *     los diez días no debe encontrarse la puerta cerrada.
 *   * **Con datos, 30 días.** Aquí sí hay algo que rescatar, y el espacio queda
 *     en **sólo lectura** para que se pueda descargar hasta el último día.
 *     Entrar **no** lo salva: la decisión la tomó un operador con un motivo
 *     escrito, y deshacerla es cosa de otro operador, no de un inicio de sesión.
 *
 * Los dos plazos son más largos que la papelera más generosa (90 días no, pero
 * sí que la de los planes gratuitos) a propósito: lo que se borra aquí no va a
 * ninguna papelera, se va del todo.
 */
export const TENANT_DELETION_DAYS = {
  /** Sin archivos: 15 días, y volver a entrar lo cancela. */
  EMPTY: 15,
  /** Con archivos: 30 días en sólo lectura, para poder llevárselos. */
  WITH_DATA: 30,
} as const;
